import {
  FormattedUpload,
  Actor,
  ValidationError,
  UploadProductSummary,
  UploadStatus,
} from "./types";
import { ApiStatementUpload, ApiUser } from "./types";

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");
}

function actorFromApiUser(
  apiUser: ApiUser | null | undefined,
): Actor | undefined {
  if (!apiUser) return undefined;
  return { name: apiUser.name, initials: initialsFromName(apiUser.name) };
}

function actorRequired(apiUser: ApiUser): Actor {
  return { name: apiUser.name, initials: initialsFromName(apiUser.name) };
}

function periodFromSheets(
  sheets: ApiStatementUpload["statementUploadSheets"],
): string {
  const dates = sheets
    .map((sheet) => sheet.valueDate)
    .filter(Boolean)
    .sort();
  if (dates.length === 0) return "";
  const first = new Date(dates[0]);
  const last = new Date(dates[dates.length - 1]);
  const monthFmt = (date: Date, long: boolean): string =>
    date.toLocaleString(undefined, { month: long ? "long" : "short" });
  if (dates.length === 1 || first.getMonth() === last.getMonth()) {
    return `${monthFmt(first, true)} ${first.getFullYear()}`;
  }
  return `${monthFmt(first, false)} – ${monthFmt(
    last,
    false,
  )} ${first.getFullYear()}`;
}

function bytesToSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) return `${kilobytes.toFixed(1)} KB`;
  const megabytes = kilobytes / 1024;
  return `${megabytes.toFixed(2)} MB`;
}

const FULLY_PROCESSED_STATUSES: ReadonlyArray<UploadStatus> = ["completed"];

function secondsDiff(start: string, end: string): number {
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(0, Math.floor(diffMs / 1000));
}

function durationLabel(
  start?: string | null,
  end?: string | null,
): string | undefined {
  if (!start || !end) return undefined;
  const totalSeconds = secondsDiff(start, end);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
}

function productsFromSheets(
  sheets: ApiStatementUpload["statementUploadSheets"],
  status: UploadStatus,
): UploadProductSummary[] {
  const finished = FULLY_PROCESSED_STATUSES.includes(status);
  return sheets.map((sheet) => ({
    name: sheet.productName,
    fundCode: sheet.fundCode,
    rows: sheet.rowCount,
    processed: finished ? sheet.rowCount : 0,
    failed: 0,
  }));
}

export function toValidationError(
  code: string,
  details: Record<string, unknown> | null,
): ValidationError {
  const safeDetails = details || {};
  const str = (key: string): string | undefined =>
    typeof safeDetails[key] === "string"
      ? (safeDetails[key] as string)
      : undefined;
  const arr = (key: string): string[] | undefined =>
    Array.isArray(safeDetails[key])
      ? (safeDetails[key] as string[])
      : undefined;

  return {
    code: code as ValidationError["code"],
    message: str("message") || code.replace(/_/g, " "),
    existingPublicId: str("public_id"),
    productName: str("product_name"),
    valueDate: str("value_date"),
    sheetName: str("sheet_name"),
    fundCode: str("normalised_fund_code") || str("fund_code"),
    sheetFundCode: str("sheet_fund_code"),
    parentFundCode: str("parent_fund_code"),
    parentName: str("parent_name"),
    weekday: str("weekday"),
    holidayName: str("holiday_name"),
    foundFundCodes: arr("found_fund_codes"),
    foundValueDates: arr("value_dates") || arr("found_value_dates"),
    expectedAccountName: str("expected_account_name"),
    foundAccountNames: arr("found_account_names"),
  } as ValidationError;
}

export function formatUploadPayload(
  apiUpload: ApiStatementUpload,
): FormattedUpload {
  const status = apiUpload.status as UploadStatus;
  const totalRows = apiUpload.statementUploadSheets.reduce(
    (acc, sheet) => acc + sheet.rowCount,
    0,
  );
  const upload: FormattedUpload = {
    id: apiUpload.publicId,
    dbId: apiUpload.id,
    filename: apiUpload.originalFilename,
    size: bytesToSize(apiUpload.byteSize),
    products: productsFromSheets(apiUpload.statementUploadSheets, status),
    period: periodFromSheets(apiUpload.statementUploadSheets),
    rows: totalRows,
    uploadedBy: actorRequired(apiUpload.uploader),
    uploadedAt: apiUpload.createdAt,
    status,
    isInceptionToDate: apiUpload.isInceptionToDate,
    needsRerun: apiUpload.needsRerun,
    totalAmount: apiUpload.totalAmount ?? undefined,
    totalUnits: apiUpload.totalUnits ?? undefined,
    validationErrors: apiUpload.validationErrors?.errors,
    validationErrorCount: apiUpload.validationErrors?.error_count,

    submittedAt: apiUpload.submittedAt ?? undefined,
    scheduledFor: apiUpload.scheduledFor ?? undefined,
    rescheduledBy: actorFromApiUser(apiUpload.rescheduledBy),
    rescheduledAt: apiUpload.rescheduledAt ?? undefined,
    rescheduleReason: apiUpload.rescheduleReason ?? undefined,

    processed: apiUpload.processedCount,
    failed: apiUpload.failedCount,
    duration: durationLabel(apiUpload.startedAt, apiUpload.finishedAt),
    error: apiUpload.errorSummary ?? undefined,
    issues: (apiUpload.issueEntries ?? []).map((entry) => ({
      reconRow: {
        id: entry.id,
        product: entry.product.name,
        productId: entry.product.id,
        fundCode: entry.product.fundCode,
        statementUploadId: entry.statementUploadId,
        priceDate: entry.priceDate,
        paymentsPriceDate: entry.paymentsPriceDate || "",
        price: entry.price?.amount ?? 0,
        amount: entry.uploadAmount,
        amountTA: entry.paymentsAmount ?? 0,
        unitsPhx: entry.uploadUnits,
        unitsTA: entry.paymentsUnits ?? 0,
        txCount: entry.txCount,
        expectedPaymentCount: entry.expectedPaymentCount,
        matchedPaymentCount: entry.matchedPaymentCount,
        missedPaymentCount: entry.missedPaymentCount,
        unitDiscrepancies: entry.unitDiscrepancies,
        created: entry.createdAt,
        match: entry.matchStatus,
        diff: entry.diffAmount ?? undefined,
        diffUnits: entry.diffUnits ?? undefined,
        reversedAt: entry.reversedAt ?? undefined,
        runType: entry.runType,
        companiesCount: entry.companiesCount,
      },
      diffAmount: entry.diffAmount,
      taCheckResult: entry.taCheckResult,
      taCheckedAt: entry.taCheckedAt,
    })),

    canceledBy: actorFromApiUser(apiUpload.canceledBy),
    canceledAt: apiUpload.canceledAt ?? undefined,
    cancelReason: apiUpload.cancelReason ?? undefined,

    reverseRequestedBy: actorFromApiUser(apiUpload.reverseRequestedBy),
    reverseRequestedAt: apiUpload.reverseRequestedAt ?? undefined,
    reverseReason: apiUpload.reverseReason ?? undefined,
    reversedAt: apiUpload.reversedAt ?? undefined,
  };
  return upload;
}
